import {
    useCreateAvatarUploadUrlMutation,
    useUpdateAvatarMutation,
} from "@api/user.api";
import {
    MAX_USER_AVATAR_FILE_SIZE_BYTES,
    MAX_USER_AVATAR_FILE_SIZE_MB,
} from "@common/constants/user";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import axios from "axios";
import { FC, PropsWithChildren, useCallback, useMemo, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { avatarUploadContext } from "./avatarUpload.context";

const AVATAR_IMAGE_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
];

const ERROR_CODES: Record<string, string> = {
    "file-too-large": `Max allowed size is ${MAX_USER_AVATAR_FILE_SIZE_MB}MB`,
    "file-invalid-type": "Only PNG, JPEG, GIF, and WEBP files are allowed",
};

export const AvatarUploadProvider: FC<PropsWithChildren> = ({ children }) => {
    const [isUploading, setIsUploading] = useState(false);
    const createAvatarUploadUrlMutation = useCreateAvatarUploadUrlMutation();
    const updateAvatarMutation = useUpdateAvatarMutation();
    const { error } = useToaster();

    const handleRejected = useCallback(
        (fileRejections: FileRejection[]) => {
            fileRejections.forEach((rejection) => {
                error(
                    ERROR_CODES[rejection.errors[0].code]
                        ? `${rejection.file.name} - ${
                              ERROR_CODES[rejection.errors[0].code]
                          }`
                        : "Unknown error when uploading file",
                );
            });
        },
        [error],
    );

    const handleAccepted = useCallback(
        async (acceptedFiles: File[]) => {
            try {
                setIsUploading(true);
                const file = acceptedFiles[0];

                const { url, path } =
                    await createAvatarUploadUrlMutation.mutateAsync(file.name);

                await axios.put(url, file, {
                    headers: {
                        "Content-Type": file.type,
                    },
                });

                await updateAvatarMutation.mutateAsync(path);
            } catch {
                error("Failed to upload avatar");
            } finally {
                setIsUploading(false);
            }
        },
        [createAvatarUploadUrlMutation, updateAvatarMutation, error],
    );

    const { getRootProps, getInputProps, isDragAccept, open } = useDropzone({
        onDropRejected: handleRejected,
        onDropAccepted: handleAccepted,
        accept: AVATAR_IMAGE_TYPES,
        maxSize: MAX_USER_AVATAR_FILE_SIZE_BYTES,
        multiple: false,
        noClick: true,
    });

    const value = useMemo(
        () => ({
            isDragActive: isDragAccept,
            inputProps: getInputProps(),
            rootProps: getRootProps(),
            isUploading,
            open,
        }),
        [isDragAccept, getInputProps, getRootProps, isUploading, open],
    );

    return (
        <avatarUploadContext.Provider value={value}>
            {children}
        </avatarUploadContext.Provider>
    );
};
